/**
 * ListSearchFilter Component
 *
 * A search and filter bar component designed to integrate with GenericListView.
 * Provides a search input and collapsible filter section with consistent styling.
 *
 * @example
 * ```tsx
 * <ListSearchFilter
 *   searchValue={searchTerm}
 *   onSearchChange={setSearchTerm}
 *   searchPlaceholder="Search users..."
 *   filters={[
 *     {
 *       key: 'group',
 *       label: 'Group',
 *       value: filterGroup,
 *       options: [
 *         { value: 'All', label: 'All Groups' },
 *         { value: 'admin', label: 'Admin' },
 *       ],
 *       onChange: setFilterGroup,
 *     },
 *   ]}
 *   resultCount={filteredUsers.length}
 *   totalCount={totalCount}
 *   loading={isLoading}
 * />
 * ```
 */
import React, { useState, useEffect } from 'react';
import { Search, Filter, ChevronDown, ChevronUp, X, Loader } from 'lucide-react';

/** Filter option definition */
export interface FilterOption {
  value: string;
  label: string;
}

/** Filter definition */
export interface FilterConfig {
  /** Unique key for the filter */
  key: string;
  /** Filter label (shown in collapsed state) */
  label: string;
  /** Current selected value */
  value: string;
  /** Available options */
  options: FilterOption[];
  /** Change handler */
  onChange: (value: string) => void;
}

export interface ListSearchFilterProps {
  /** Current search value */
  searchValue?: string;
  /** Search change handler */
  onSearchChange?: (value: string) => void;
  /** Search input placeholder */
  searchPlaceholder?: string;
  /** Filter configurations */
  filters?: FilterConfig[];
  /** Number of results after filtering */
  resultCount?: number;
  /** Total number of items (before filtering/pagination) */
  totalCount?: number;
  /** Whether data is loading */
  loading?: boolean;
  /** Storage key for filter expanded state persistence */
  storageKey?: string;
  /** Custom class name */
  className?: string;
  /** Render filters inline with the search control on desktop */
  desktopInline?: boolean;
  /** Start the desktop filter controls collapsed */
  desktopCollapsedByDefault?: boolean;
  /** Hide the result count footer on desktop */
  hideDesktopResultCount?: boolean;
  /** Render only the desktop search control */
  desktopToolbarSearchOnly?: boolean;
  /** Render only the expanded desktop filter controls */
  desktopFilterPanelOnly?: boolean;
  /** Controlled desktop filter expansion state */
  desktopFilterExpanded?: boolean;
  /** Controlled desktop filter expansion handler */
  onDesktopFilterExpandedChange?: (expanded: boolean) => void;
  /** Render the legacy search/filter layout only on mobile */
  mobileOnly?: boolean;
}

function ListSearchFilter({
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  filters = [],
  resultCount,
  totalCount,
  loading = false,
  storageKey,
  className = '',
  desktopInline = false,
  desktopCollapsedByDefault = false,
  hideDesktopResultCount = false,
  desktopToolbarSearchOnly = false,
  desktopFilterPanelOnly = false,
  desktopFilterExpanded,
  onDesktopFilterExpandedChange,
  mobileOnly = false,
}: ListSearchFilterProps) {
  // Track filter expansion state with optional persistence
  const [isFilterExpanded, setIsFilterExpanded] = useState(() => {
    if (storageKey) {
      const saved = localStorage.getItem(`${storageKey}_filtersExpanded`);
      return saved ? JSON.parse(saved) : true;
    }
    return true;
  });
  const [isDesktopFilterExpanded, setIsDesktopFilterExpanded] = useState(() => {
    if (storageKey) {
      const saved = localStorage.getItem(`${storageKey}_desktopFiltersExpanded`);
      return saved ? JSON.parse(saved) : !desktopCollapsedByDefault;
    }
    return !desktopCollapsedByDefault;
  });
  const desktopExpanded = desktopFilterExpanded ?? isDesktopFilterExpanded;
  const setDesktopExpanded = (expanded: boolean) => {
    onDesktopFilterExpandedChange?.(expanded);
    if (desktopFilterExpanded === undefined) {
      setIsDesktopFilterExpanded(expanded);
    }
  };

  // Persist filter expanded state
  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(`${storageKey}_filtersExpanded`, JSON.stringify(isFilterExpanded));
    }
  }, [isFilterExpanded, storageKey]);

  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(`${storageKey}_desktopFiltersExpanded`, JSON.stringify(isDesktopFilterExpanded));
    }
  }, [isDesktopFilterExpanded, storageKey]);

  // Check if any filter is active (not default/all)
  const activeFilterCount = filters.filter(
    (f) => f.value !== 'All' && f.value !== '' && f.value !== f.options[0]?.value
  ).length;

  const hasActiveFilters = activeFilterCount > 0 || searchValue.length > 0;

  // Clear all filters
  const clearFilters = () => {
    if (onSearchChange) {
      onSearchChange('');
    }
    filters.forEach((filter) => {
      // Reset to first option (usually "All")
      if (filter.options.length > 0) {
        filter.onChange(filter.options[0].value);
      }
    });
  };

  // Don't render if no search or filters configured
  if (!onSearchChange && filters.length === 0) {
    return null;
  }

  if (desktopToolbarSearchOnly) {
    return onSearchChange ? (
      <div className="relative w-full sm:max-w-xs flex-none">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <Search size={15} className="text-slate-400" />
        </div>
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-9 pl-9 pr-8 w-full text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors bg-white shadow-sm"
          aria-label="Search"
        />
        {searchValue && (
          <button onClick={() => onSearchChange('')} className="absolute inset-y-0 right-0 flex items-center pr-2 text-slate-400 hover:text-slate-600" aria-label="Clear search">
            <X size={14} />
          </button>
        )}
      </div>
    ) : null;
  }

  if (desktopFilterPanelOnly) {
    return filters.length > 0 && desktopExpanded ? (
      <div className="border-t border-slate-200 pt-3 mt-3 mb-4">
        <div className="flex flex-wrap gap-2">
          {filters.map((filter) => (
            <div key={filter.key} className="w-40 flex-shrink-0">
              <select value={filter.value} onChange={(e) => filter.onChange(e.target.value)} className="h-9 w-full px-2.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors bg-white shadow-sm" aria-label={`Filter by ${filter.label}`}>
                {filter.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>
    ) : null;
  }

  return (
    <div className={`${mobileOnly ? 'hidden ' : ''}bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3 ${className}`}>
      {desktopInline && (
        <div className="hidden md:flex items-center gap-2">
          {onSearchChange && (
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search size={15} className="text-slate-400" />
              </div>
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                className="h-9 pl-9 pr-8 w-full text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors bg-white shadow-sm"
                aria-label="Search"
              />
              {searchValue && (
                <button
                  onClick={() => onSearchChange('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-2 text-slate-400 hover:text-slate-600"
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          )}
          {desktopExpanded && filters.map((filter) => (
            <div key={filter.key} className="w-40 flex-shrink-0">
              <select
                value={filter.value}
                onChange={(e) => filter.onChange(e.target.value)}
                className="h-9 w-full px-2.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors bg-white shadow-sm"
                aria-label={`Filter by ${filter.label}`}
              >
                {filter.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
          {filters.length > 0 && (
            <button
              onClick={() => setDesktopExpanded(!desktopExpanded)}
              className={`h-9 flex items-center justify-center px-1.5 rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 flex-shrink-0 ${
                activeFilterCount > 0
                  ? 'bg-slate-700 text-white border-slate-700 hover:bg-slate-600'
                  : 'text-slate-600 border-slate-300 bg-white hover:bg-slate-100'
              }`}
              aria-expanded={desktopExpanded}
              aria-label={desktopExpanded ? 'Collapse filters' : 'Expand filters'}
              title={desktopExpanded ? 'Collapse filters' : 'Expand filters'}
            >
              <Filter size={14} />
              {desktopExpanded ? <ChevronUp size={14} className="ml-1" /> : <ChevronDown size={14} className="ml-1" />}
            </button>
          )}
        </div>
      )}

      {/* Search bar and filter toggle */}
      <div className={`${desktopInline ? 'md:hidden ' : ''}flex items-center gap-3`}>
        {/* Search input */}
        {onSearchChange && (
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search size={16} className="text-slate-400" />
            </div>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-9 pr-8 py-2 w-full text-base border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors bg-white shadow-sm"
              aria-label="Search"
            />
            {searchValue && (
              <button
                onClick={() => onSearchChange('')}
                className="absolute inset-y-0 right-0 flex items-center pr-2 text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}

        {/* Filter toggle button */}
        {filters.length > 0 && (
          <button
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            className={`flex items-center px-3 py-2 rounded-md border transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-slate-400 whitespace-nowrap ${
              activeFilterCount > 0
                ? 'bg-slate-700 text-white border-slate-700 hover:bg-slate-600'
                : 'text-slate-600 border-slate-300 bg-white hover:bg-slate-100'
            }`}
            aria-expanded={isFilterExpanded}
            aria-label={isFilterExpanded ? 'Collapse filters' : 'Expand filters'}
          >
            <Filter size={14} className="mr-1.5" />
            Filters
            {activeFilterCount > 0 && (
              <span className="flex items-center justify-center w-5 h-5 ml-1.5 bg-white text-slate-700 text-xs font-bold rounded-full">
                {activeFilterCount}
              </span>
            )}
            {isFilterExpanded ? (
              <ChevronUp size={14} className="ml-1.5" />
            ) : (
              <ChevronDown size={14} className="ml-1.5" />
            )}
          </button>
        )}
      </div>

      {/* Collapsible filter section */}
      {filters.length > 0 && (
        <div
          className={`${desktopInline ? 'md:hidden ' : ''}overflow-hidden transition-all duration-200 ease-in-out ${
            isFilterExpanded ? 'max-h-40 opacity-100 mt-3' : 'max-h-0 opacity-0'
          }`}
          style={{ visibility: isFilterExpanded ? 'visible' : 'hidden' }}
        >
          <div className="border-t border-slate-200 pt-3">
            <div className="flex flex-wrap gap-2">
              {filters.map((filter) => (
                <div key={filter.key} className="min-w-[140px]">
                  <select
                    value={filter.value}
                    onChange={(e) => filter.onChange(e.target.value)}
                    className="w-full px-3 py-1.5 text-base border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors bg-white shadow-sm"
                    aria-label={`Filter by ${filter.label}`}
                  >
                    {filter.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Result count and clear filters */}
      {(resultCount !== undefined || hasActiveFilters) && (
        <div className={`${hideDesktopResultCount ? (mobileOnly ? 'hidden ' : 'md:hidden ') : ''}flex justify-between items-center mt-2 pt-2 border-t border-slate-200 text-xs text-slate-500`}>
          <span className="font-medium">
            {loading ? (
              <span className="flex items-center">
                <Loader className="animate-spin mr-1" size={12} />
                Loading...
              </span>
            ) : resultCount !== undefined ? (
              <>
                {resultCount} result{resultCount !== 1 ? 's' : ''}
                {totalCount !== undefined && totalCount > resultCount && (
                  <span className="text-slate-400"> of {totalCount}</span>
                )}
              </>
            ) : null}
          </span>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-slate-600 hover:text-slate-800 flex items-center gap-0.5 font-medium transition-colors"
              aria-label="Clear all filters"
              disabled={loading}
            >
              <X size={12} />
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default ListSearchFilter;
